import React, { FC, ReactNode } from 'react';

import { Checkbox } from '~/components/checkbox';

interface ConfirmationBoxProps {
  title: string;
  description?: ReactNode;
  isChecked: boolean;
  onChange: (checked: boolean) => void;
}

/**
 * A confirmation that is loud while outstanding.
 *
 * Accepting a default or waving through a blank field should be a deliberate act, so an
 * unticked confirmation renders amber and says it is expecting a tick — visible at a
 * glance, rather than discoverable only by wondering why the commit button is disabled.
 */
export const ConfirmationBox: FC<ConfirmationBoxProps> = ({
  title,
  description,
  isChecked,
  onChange,
}) => {
  const containerClass = isChecked
    ? 'bg-green-lightest border border-green'
    : 'bg-yellow-lightest border border-yellow';

  return (
    <div className={`flex items-start gap-3 rounded p-4 ${containerClass}`}>
      <div className="mt-0.5 flex-shrink-0">
        <Checkbox isChecked={isChecked} onChange={() => onChange(!isChecked)} />
      </div>
      <div className="flex-grow">
        <div className="flex items-center justify-between">
          <p className="text-base font-semibold">{title}</p>
          <p
            className={`ml-4 flex-shrink-0 text-sm font-semibold ${
              isChecked ? 'text-green-dark' : 'text-yellow'
            }`}
          >
            {isChecked ? 'Confirmed ✓' : 'Outstanding — expecting a tick here'}
          </p>
        </div>
        {!!description && (
          <div className="mt-1 text-sm text-primary-500">{description}</div>
        )}
      </div>
    </div>
  );
};
